/* Kashira toast system — 17 System Feedback contract:
   bottom-center (24px up desktop, above safe-area mobile), max ONE
   visible (new replaces old), enter rise+fade 250ms / exit fade 200ms,
   5s timeout (8s under reduced motion), hover pauses, UNDO on
   destructive actions, aria-live="polite".

   Three variants:
     undo         — action slot + drain hairline, 5s (pass actionLabel/onAction)
     confirmation — green tick disc, no action slot, 3s (the spec makes this one
                    shorter precisely because there is nothing to click)
     warning      — amber disc, 5s (qty cap, COD ceiling)

   API: window.KashiraToast.show(message, { actionLabel, onAction, variant })
        variant: 'confirmation' (alias 'success') | 'warning' | omitted
        returns a handle with .dismiss() */
(() => {
  if (window.KashiraToast) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const TIMEOUTS = {
    default: reduced ? 8000 : 5000,
    // The confirmation has no action to reach, so it takes the spec's shorter
    // window — scaled the same way under reduced motion.
    success: reduced ? 4800 : 3000,
  };

  let current = null;

  /* Built and attached once at boot rather than on first use: a live region
     that is inserted into the document and filled inside the same task can lose
     the announcement on some screen readers. */
  const region = document.createElement('div');
  region.className = 'kashira-toast-region';
  region.setAttribute('aria-live', 'polite');

  const ensureRegion = () => {
    if (!region.isConnected && document.body) document.body.appendChild(region);
  };
  if (document.body) ensureRegion();
  else document.addEventListener('DOMContentLoaded', ensureRegion, { once: true });

  const dismiss = (el) => {
    if (!el || el.dataset.leaving) return;
    el.dataset.leaving = '1';
    clearTimeout(el._timer);
    el.classList.add('kashira-toast--leaving');
    const remove = () => el.remove();
    if (reduced) {
      remove();
    } else {
      // Ignore the drain hairline's own animationend — only the exit fade ends the toast.
      const onEnd = (event) => {
        if (event.pseudoElement) return;
        el.removeEventListener('animationend', onEnd);
        remove();
      };
      el.addEventListener('animationend', onEnd);
      // animationend is not guaranteed (throttled frames, interrupted or
      // restarted animations — observed with focus inside the toast); a
      // missed event must never strand a zombie toast over the page.
      setTimeout(remove, 400);
    }
    if (current === el) current = null;
  };

  /* Resumes with the time that was left, not a fresh window — the CSS drain
     hairline resumes rather than restarting, and the two must agree. */
  const startTimer = (el, ms) => {
    /* A pointerenter landing before the first startTimer reads an unset
       _deadline, so guard on finiteness rather than typeof — NaN is a number. */
    const life = Number.isFinite(ms) ? Math.max(0, ms) : el._life;
    el._deadline = Date.now() + life;
    el._timer = setTimeout(() => dismiss(el), life);
  };

  // Glyphs for the status disc: tick for confirmations, bar+dot for limits.
  const ICON_PATHS = {
    success: '<path d="M5 12.5l4.5 4.5L19 7"/>',
    warning: '<path d="M12 6v7"/><path d="M12 17.2v.1"/>',
  };
  /* 'confirmation' is the spec's name for the green-disc toast; the glyph key
     and the modifier class stay `success` because that is what the CSS draws
     and what the existing call sites pass. */
  const ALIAS = { confirmation: 'success' };

  window.KashiraToast = {
    show(message, { actionLabel, onAction, variant, focusAction } = {}) {
      ensureRegion();
      /* Max ONE visible: the outgoing toast goes immediately rather than fading
         out underneath the new one and briefly stacking two cards. */
      if (current) {
        clearTimeout(current._timer);
        current.remove();
        current = null;
      }

      const kind = ALIAS[variant] || variant;
      const hasAction = Boolean(actionLabel && onAction);
      const el = document.createElement('div');
      el.className = 'kashira-toast';

      const life = kind === 'success' && !hasAction ? TIMEOUTS.success : TIMEOUTS.default;
      el._life = life;
      // Keeps the drain hairline in step with the timer, reduced motion included.
      el.style.setProperty('--kashira-toast-life', `${life}ms`);

      if (ICON_PATHS[kind]) {
        el.classList.add(`kashira-toast--${kind}`);
        const icon = document.createElement('span');
        icon.className = 'kashira-toast__icon';
        icon.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">${ICON_PATHS[kind]}</svg>`;
        el.appendChild(icon);
      }

      const text = document.createElement('span');
      text.className = 'kashira-toast__text';
      text.textContent = message;
      el.appendChild(text);

      if (hasAction) {
        el.classList.add('kashira-toast--undo');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'kashira-toast__action';
        btn.textContent = actionLabel;
        btn.addEventListener('click', () => {
          onAction();
          dismiss(el);
        });
        el.appendChild(btn);
      }

      // Keyboard parity with the pointer pause: while focus is anywhere in
      // the toast the countdown holds, so the action stays reachable at a
      // keyboard user's pace (WCAG 2.2.1).
      const pause = () => {
        clearTimeout(el._timer);
        el._remaining = el._deadline - Date.now();
        el.classList.add('is-paused');
      };
      const resume = () => {
        el.classList.remove('is-paused');
        startTimer(el, el._remaining);
      };
      el.addEventListener('pointerenter', pause);
      el.addEventListener('pointerleave', resume);
      el.addEventListener('focusin', pause);
      el.addEventListener('focusout', (event) => {
        if (!el.contains(event.relatedTarget)) resume();
      });

      region.appendChild(el);
      current = el;
      startTimer(el);
      // A keyboard-initiated flow may hand focus straight to the action so
      // the toast is operable without a pointer.
      if (focusAction) el.querySelector('.kashira-toast__action')?.focus();
      return { dismiss: () => dismiss(el) };
    },
  };
})();
